import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BufferService, LoadingContext, M_Button, M_ElementDrawer, M_Header, M_MessageBoard, M_Table, NavigateContext } from 'src/M_Components'
import { ShipmentProcess } from './components/ShipmentProcess'
import { ShipmentQuote } from './components/ShipmentQuote'
import { ShipmentPackages } from './components/ShipmentPackages'
import { ShipmentDangerousGoods } from './components/ShipmentDangerousGoods'

export const ShipmentDetail = ({ roles }) => {
    const ctx = useContext(LoadingContext)
    const navigateCtx = useContext(NavigateContext)
    const { t } = useTranslation()

    const shipmentService = new BufferService('shipment')
    const viewbookingService = new BufferService('viewbooking')

    const [data, setData] = useState({})
    const [operation, setOperation] = useState('')
    const [tabId, setTabId] = useState(sessionStorage.getItem('selectedTab'))
    const [sourceTabUrl, setSourceTabUrl] = useState('')
    const [isFirstTime, setIsFirstTime] = useState(true)
    const [showPageCloseModal, setShowPageCloseModal] = useState(false)
    const [bookingDrawerOpen, setBookingDrawerOpen] = useState(false)
    const autoOpenedRef = useRef(false)

    const sessionKeys = ['operation', 'data']

    useEffect(() => {
        const passOperation = ctx.passValue.get('operation')
        setOperation(passOperation)

        if (isFirstTime) {
            ctx.setIsLoad(true)
            getSessionStorageData(tabId)
            setIsFirstTime(false)
            sessionStorage.setItem(`tab${tabId}`, JSON.stringify(sessionKeys))
        }
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        const newId = sessionStorage.getItem('selectedTab')
        if (newId !== tabId) {
            setTabId(newId)
            getSessionStorageData(newId)
        }
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        sessionKeys.forEach((key) => sessionStorageMachine(tabId, key, key))
        // eslint-disable-next-line
    }, [tabId, data, operation])

    useEffect(() => {
        if (operation === 'create' && !autoOpenedRef.current && !data.IdBooking) {
            autoOpenedRef.current = true
            setBookingDrawerOpen(true)
        }
        // eslint-disable-next-line
    }, [operation])

    const sessionStorageMachine = (id, key, value) => {
        if (eval(value) instanceof Object && Object.keys(eval(value)).length === 0) return
        if (eval(value) instanceof String && eval(value) === '') return
        // eslint-disable-next-line
        sessionStorage.setItem(`${id}${key}`, JSON.stringify(eval(value)))
    }

    const getSessionStorageData = (sessionID) => {
        ctx.setIsLoad(true)
        const tempO = JSON.parse(sessionStorage.getItem(`${sessionID}operation`))
        const tempD = JSON.parse(sessionStorage.getItem(`${sessionID}data`))

        if ((tempO && tempO !== '') || (tempD && Object.keys(tempD).length !== 0)) {
            setOperation(String(tempO))
            setData({ ...tempD })
            ctx.setIsLoad(false)
            return
        }
        get()
    }

    const get = () => {
        const passOperation = ctx.passValue.get('operation')
        const passSourceUrl = ctx.passValue.get('sourceTabUrl')
        if (passSourceUrl) {
            setSourceTabUrl(passSourceUrl)
            sessionStorage.setItem(`${tabId}sourceTabUrl`, passSourceUrl)
        } else {
            const stored = sessionStorage.getItem(`${tabId}sourceTabUrl`)
            if (stored) setSourceTabUrl(stored)
        }

        if (passOperation === 'create') {
            ctx.setIsLoad(false)
            return
        }

        const row = ctx.passValue.get('Row')
        if (!row) { ctx.setIsLoad(false); return }

        shipmentService.getId(row.Id).then((response) => {
            if (response.name === 'AxiosError') {
                ctx.toastMessages('error', '', response?.response?.data?.Errors || response.message)
                ctx.setIsLoad(false)
                return
            }

            let shipmentData
            if (passOperation === 'copy') {
                const { Id, DbStatus, TransportKey, ...copyData } = response
                shipmentData = copyData
            } else {
                shipmentData = response
            }
            setData({ ...shipmentData })
            ctx.setIsLoad(false)

            // Fetch booking to populate display-only derived fields (not stored in Shipment entity)
            if (shipmentData.IdBooking) {
                const bookingService = new BufferService('booking')
                bookingService.getId(shipmentData.IdBooking).then(booking => {
                    if (booking && booking.name !== 'AxiosError') {
                        setData(prev => ({
                            ...prev,
                            ProcessKey: booking.ProcessKey || '',
                            VesselCode: booking.VesselCode || '',
                            VoyageNumber: booking.VoyageNumber || '',
                            LineCode: booking.LineCode || '',
                            ServiceCode: booking.ServiceCode || '',
                        }))
                    }
                })
            }

            // Fetch quote to populate QuoteCode (not stored in Shipment entity, Quote is in different context)
            if (shipmentData.IdQuote) {
                const quoteService = new BufferService('quote')
                quoteService.getId(shipmentData.IdQuote).then(quote => {
                    if (quote && quote.name !== 'AxiosError') {
                        setData(prev => ({
                            ...prev,
                            QuoteCode: quote.QuoteCode || '',
                        }))
                    }
                })
            }
        }).catch((err) => {
            ctx.toastMessages('error', '', err.message)
            ctx.setIsLoad(false)
        })
    }

    const onBookingPassLine = async (lines) => {
        const booking = lines?.[0]
        if (!booking) return
        setBookingDrawerOpen(false)

        setData(prev => ({
            ...prev,
            // Booking fields
            IdBooking: booking.Id,
            BookingNumber: booking.BookingNumber,
            ProcessKey: booking.ProcessKey || '',
            VesselCode: booking.VesselCode || '',
            VoyageNumber: booking.VoyageNumber || '',
            // Trade fields (populated from SQL view)
            LineCode: booking.LineCode || '',
            ServiceCode: booking.ServiceCode || '',
        }))
    }

    const buildPayload = () => ({
        IdBooking: data.IdBooking,
        IdQuote: data.IdQuote || null,
        IdPackage: data.IdPackage || null,
        IdInnerPackage: data.IdInnerPackage || null,
        IdCommodity: data.IdCommodity || null,
        IdDangerousGoods: data.IdDangerousGoods || null,
        IdCustomStatus: data.IdCustomStatus || null,
        MotherBound: data.MotherBound || '',
        PayableHow: data.PayableHow || '',
        FreightLineReference: data.FreightLineReference || '',
        FreightReference: data.FreightReference || '',
        Revision: data.Revision != null ? Number(data.Revision) : null,
        Order: data.Order != null ? Number(data.Order) : null,
        ShipmentDescription: data.ShipmentDescription || '',
        NrPacks: data.NrPacks != null ? Number(data.NrPacks) : null,
        NrInPacks: data.NrInPacks != null ? Number(data.NrInPacks) : null,
        GrossWeight: data.GrossWeight != null ? Number(data.GrossWeight) : null,
        NetWeight: data.NetWeight != null ? Number(data.NetWeight) : null,
        Measures: data.Measures != null ? Number(data.Measures) : null,
        MinTemp: data.MinTemp != null ? Number(data.MinTemp) : null,
        Temp: data.Temp != null ? Number(data.Temp) : null,
        MaxTemp: data.MaxTemp != null ? Number(data.MaxTemp) : null,
        UCR: data.UCR || '',
        UCN: data.UCN || '',
        FlashPT: data.FlashPT != null ? Number(data.FlashPT) : null,
        FlashPTUnit: data.FlashPTUnit || '',
        MP: data.MP === true ? true : data.MP === false ? false : null,
        PackingGroup: data.PackingGroup || '',
        ShipmentLimitedQuantities: data.ShipmentLimitedQuantities === true ? true : data.ShipmentLimitedQuantities === false ? false : null,
        MarineWeight: data.MarineWeight != null ? Number(data.MarineWeight) : null,
        MarineDescription: data.MarineDescription || '',
        ApprovalNumber: data.ApprovalNumber || '',
        IMODangerGoods: data.IMODangerGoods || false,
    })

    const onSave = async () => {
        if (!data.IdBooking) {
            ctx.toastMessages('error', '', `${t('BookingNumber')} ${t('FieldIsRequired')}`)
            return
        }

        ctx.setIsLoad(true)
        const payload = buildPayload()

        if (operation === 'create' || operation === 'copy') {
            shipmentService.post(payload).then((response) => {
                if (response.name === 'AxiosError') {
                    const resData = response?.response?.data
                    ctx.toastMessages('error', '', resData ? Object.values(resData).flat().join('\n') || response.message : response.message)
                    ctx.setIsLoad(false)
                } else {
                    ctx.toastMessages('success', '', t('CreatedShipment'))
                    ctx.setIsLoad(false)
                    navigateCtx.closeTab(tabId.replace(/"/g, ''), sourceTabUrl || undefined)
                }
            })
        } else if (operation === 'edit') {
            shipmentService.update(data.Id, payload).then((response) => {
                if (response.name === 'AxiosError') {
                    const resData = response?.response?.data
                    ctx.toastMessages('error', '', resData ? Object.values(resData).flat().join('\n') || response.message : response.message)
                    ctx.setIsLoad(false)
                } else {
                    ctx.toastMessages('success', '', t('EditedShipment'))
                    ctx.setIsLoad(false)
                    navigateCtx.closeTab(tabId.replace(/"/g, ''), sourceTabUrl || undefined)
                }
            })
        }
    }

    const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 700)
    useEffect(() => {
        const handleResize = () => setIsNarrow(window.innerWidth < 700)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    return (
        <div>
            <M_Header
                element={
                    <>
                        <M_Button
                            tooltip={t('Back')}
                            label={t('Back')}
                            icon={<i className='pi pi-arrow-left iconTable' style={{ width: '20px', color: ctx.secondaryColor || 'var(--color-black)' }}></i>}
                            onClick={() => setShowPageCloseModal(true)}
                        />
                        <M_Button
                            tooltip={t('Save')}
                            icon={<i className='pi pi-save iconTable'></i>}
                            label={t('Save')}
                            onClick={onSave}
                        />
                    </>
                }
            />

            <div style={{ margin: '10px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>

                <div style={{ display: 'flex', flexDirection: isNarrow ? 'column' : 'row', gap: '10px', alignItems: 'stretch' }}>
                    <div style={{ flex: '1 1 0px', minWidth: 0 }}>
                        <ShipmentProcess
                            dataState={data}
                            setDataState={setData}
                            operation={operation}
                            onOpenBookingDrawer={() => setBookingDrawerOpen(true)}
                        />
                    </div>
                    <div style={{ flex: '1 1 0px', minWidth: 0 }}>
                        <ShipmentQuote dataState={data} setDataState={setData} />
                    </div>
                </div>

                <ShipmentPackages dataState={data} setDataState={setData} />

                <ShipmentDangerousGoods dataState={data} setDataState={setData} />
            </div>

            <M_ElementDrawer
                open={bookingDrawerOpen}
                setOpen={setBookingDrawerOpen}
                onClose={() => setBookingDrawerOpen(false)}
                element={
                    <>
                        <h2 style={{ marginLeft: '10px', marginTop: '0px', marginBottom: '10px' }}>{t('BookingNumber')}</h2>
                        <M_Table
                            entityName={'booking'}
                            useSessionStorage={false}
                            crud={false}
                            selectSingle={true}
                            onPassLine={onBookingPassLine}
                        />
                    </>
                }
            />

            <M_MessageBoard
                warning
                open={showPageCloseModal}
                onClose={() => setShowPageCloseModal(false)}
                title={t('leavingPageTitle')}
                body={t('leavingPageBody')}
                footer={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                        <M_Button hoverEffect normal labelColor={'white'} backgroundColor={'var(--color-red)'} onClick={() => setShowPageCloseModal(false)} label={t('ButtonCancel')} />
                        <M_Button hoverEffect normal labelColor={'white'} backgroundColor={'var(--color-green)'} onClick={() => navigateCtx.closeTab(JSON.parse(tabId))} label={t('ButtonConfirm')} />
                    </span>
                }
            />
        </div>
    )
}
